import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import { GoldButton } from "@/components/admin/AdminLayout";
import { toast } from "sonner";
import { Percent, Settings, CheckCircle2, Info, Route, Map, Car } from "lucide-react";

const SERVICE_TYPES = [
  { k: "tours", l: "Tours", icon: Map, markupField: "tour_markup",
    desc_custom: "Definir preços directos por tour/categoria/temporada em Tours.",
    desc_markup: "Aplicar margem % ou EUR sobre os preços DiTours dos tours." },
  { k: "routes", l: "Rotas Fixas", icon: Route, markupField: "route_markup",
    desc_custom: "Definir preços directos por rota/categoria/temporada em Rotas Fixas.",
    desc_markup: "Aplicar margem % ou EUR sobre os preços DiTours das rotas." },
  { k: "transfers", l: "Transferes Dinâmicos", icon: Car, markupField: "transfer_markup",
    desc_custom: "Definir tarifários próprios em Categorias de Veículos (base + km + min).",
    desc_markup: "Aplicar margem % ou EUR sobre os tarifários DiTours." },
];

export default function PartnerMarkup() {
  const [me, setMe] = useState(null);
  const [markup, setMarkup] = useState({ mode: "eur" });
  const [modes, setModes] = useState({ tours: "custom", routes: "custom", transfers: "custom" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/partner/me").then((r) => {
      setMe(r.data);
      setMarkup(r.data.markup || { mode: "eur" });
      const fallback = r.data.pricing_mode || "custom";
      setModes({
        tours: r.data.pricing_mode_tours || fallback,
        routes: r.data.pricing_mode_routes || fallback,
        transfers: r.data.pricing_mode_transfers || fallback,
      });
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/partner/markup", {
        markup,
        pricing_mode_tours: modes.tours,
        pricing_mode_routes: modes.routes,
        pricing_mode_transfers: modes.transfers,
      });
      toast.success("Guardado");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!me) return <div className="p-10 text-slate-500">A carregar…</div>;

  const anyMarkup = modes.tours === "markup" || modes.routes === "markup" || modes.transfers === "markup";

  return (
    <div className="p-10 max-w-[900px]" data-testid="partner-markup-page">
      <PortalPageHeader
        eyebrow="Pricing"
        title="Estratégia de preços"
        description="Por cada tipo de serviço escolha se quer definir preços próprios ou aplicar uma margem sobre os preços DiTours. Pode misturar (ex: margem em Rotas, preços directos em Tours)."
      >
        <GoldButton onClick={save} disabled={saving} data-testid="save-markup">
          {saving ? "A guardar…" : "Guardar"}
        </GoldButton>
      </PortalPageHeader>

      {/* Global markup unit (eur / pct) — only visible when any service uses markup */}
      {anyMarkup && (
        <div className="bg-white border border-slate-200 shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-slate-100">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Unidade da margem (comum a todos os serviços)</p>
          </div>
          <div className="p-6">
            <div className="flex gap-2">
              {[{ k: "eur", l: "EUR fixo" }, { k: "pct", l: "Percentagem %" }].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setMarkup({ ...markup, mode: m.k })}
                  data-testid={`markup-mode-${m.k}`}
                  className={`flex-1 py-3 text-xs uppercase tracking-wide border transition-colors ${markup.mode === m.k ? "border-[#f59e0b] text-[#f59e0b] font-semibold bg-[#f59e0b]/5" : "border-slate-300 text-slate-500 hover:border-slate-400"}`}
                >
                  {m.l}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Per-service configuration */}
      <div className="bg-white border border-slate-200 shadow-sm mb-6">
        <div className="px-6 py-4 border-b border-slate-100">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Modo por tipo de serviço</p>
        </div>
        <div className="p-6 space-y-4">
          {SERVICE_TYPES.map((s) => {
            const Icon = s.icon;
            const isMarkup = modes[s.k] === "markup";
            return (
              <div key={s.k} className="border border-slate-200">
                <div className="p-4 grid grid-cols-1 md:grid-cols-[220px_1fr_1fr] gap-3 items-stretch">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-[#f59e0b]" />
                    <div className="text-sm font-bold text-[#0a0f1e]">{s.l}</div>
                  </div>
                  <ModeOption
                    active={modes[s.k] === "custom"}
                    onSelect={() => setModes({ ...modes, [s.k]: "custom" })}
                    icon={<Settings className="w-3.5 h-3.5" />}
                    title="Preços directos"
                    description={s.desc_custom}
                    testId={`mode-${s.k}-custom`}
                  />
                  <ModeOption
                    active={isMarkup}
                    onSelect={() => setModes({ ...modes, [s.k]: "markup" })}
                    icon={<Percent className="w-3.5 h-3.5" />}
                    title="Margem sobre DiTours"
                    description={s.desc_markup}
                    testId={`mode-${s.k}-markup`}
                  />
                </div>

                {/* Inline margin input right below the service */}
                {isMarkup && (
                  <div className="border-t border-slate-200 bg-[#fffbeb] px-4 py-4">
                    <label className="block text-[11px] uppercase tracking-widest text-slate-600 mb-2 font-bold">
                      Margem para {s.l} · {markup.mode === "eur" ? "€ fixo" : "% percentagem"}
                    </label>
                    <div className="flex items-stretch max-w-[280px] border border-slate-300 focus-within:border-[#f59e0b] focus-within:ring-2 focus-within:ring-[#f59e0b]/20 transition-colors bg-white">
                      <span className="flex items-center justify-center px-3 bg-slate-50 text-slate-500 text-sm font-mono border-r border-slate-300">
                        {markup.mode === "eur" ? "€" : "%"}
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={markup[s.markupField] ?? 0}
                        onChange={(e) => setMarkup({ ...markup, [s.markupField]: Number(e.target.value) })}
                        className="flex-1 px-3 py-2 text-base outline-none bg-white"
                        style={{ MozAppearance: "textfield" }}
                        data-testid={`markup-${s.markupField}`}
                      />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2 leading-tight">
                      {markup.mode === "eur"
                        ? `Adiciona ${markup[s.markupField] || 0}€ ao preço DiTours de cada ${s.l.toLowerCase()}.`
                        : `Adiciona ${markup[s.markupField] || 0}% ao preço DiTours de cada ${s.l.toLowerCase()}.`}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 bg-[#fefce8] border border-amber-200 p-4 text-sm text-amber-900 flex gap-3">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold mb-1">Como funciona</div>
          {anyMarkup ? (
            <div>
              Para os tipos com <strong>margem sobre DiTours</strong>, o sistema usa os preços actuais do DiTours (com temporadas activas) e adiciona a sua margem.
              Para os tipos com <strong>preços directos</strong>, usa o que configurar em Tours, Rotas Fixas ou Categorias de Veículos.
            </div>
          ) : (
            <div>
              Todos os preços vêm da configuração que fez em <strong>Tours</strong>, <strong>Rotas Fixas</strong> e <strong>Categorias de Veículos</strong>. Se deixou vazio numa célula, é usado o preço mínimo sugerido.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModeOption({ active, onSelect, icon, title, description, testId }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left border p-3 relative transition-colors ${active ? "border-[#f59e0b] bg-[#f59e0b]/5 ring-2 ring-[#f59e0b]/20" : "border-slate-200 hover:border-slate-300 bg-white"}`}
      data-testid={testId}
    >
      {active && (
        <div className="absolute top-2 right-2 text-[#f59e0b]">
          <CheckCircle2 className="w-4 h-4" />
        </div>
      )}
      <div className="flex items-center gap-2 mb-1">
        <span className={active ? "text-[#f59e0b]" : "text-slate-400"}>{icon}</span>
        <span className={`text-sm font-bold ${active ? "text-[#0a0f1e]" : "text-slate-700"}`}>{title}</span>
      </div>
      <p className="text-[10px] text-slate-500 leading-tight pl-6">{description}</p>
    </button>
  );
}
